import { defineStore } from "pinia";
import { markRaw, Raw } from "vue";
import { sdk, idToChannelName } from "@/js/sdk";
import { SdkError, ERROR_CODES, type TaskStatus as SdkTaskStatus } from "@signaloid/cloud-compute-engine-client";
import { isTaskActive, isTaskTerminated, Task, TaskStatus } from "@/types/api/tasks";
import { useEventBus, UseEventBusReturn } from "@vueuse/core";
import * as Sentry from "@sentry/vue";

import type { EventBusKey } from "@vueuse/core";
import { RemoteAccessState, TaskBusEvent, TaskEventE } from "@/types/general";
import { useUserStore } from "./user";

export const taskKey: EventBusKey<TaskBusEvent> = Symbol("symbol-key");
export const TASK_LIST_FORBIDDEN_MESSAGE = "Your account does not have access to these tasks.";

import * as util from "@/js/utilities";
import { UserLimitsE } from "@/js/tierconfig";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { useRootStore } from "@/stores/root";
import { useRealtimeStore } from "@/stores/realtime";
import { isRealtimeGiveUpError } from "@/js/sdkError";
import { PartialTask } from "@/js/signaloidClient.types";
import {
	LIST_CACHE_MAX_ENTRIES,
	hydrateCachedList,
	mirrorStoreListToCache,
	readCachedRecord,
	writeCachedRecord,
} from "@/js/listCache";

type RealtimeSubscription = Awaited<ReturnType<ReturnType<typeof sdk.realtime.channel>["subscribe"]>>;

// Realtime frames deliver the event as a JSON string; id/status sit under its `data`.
type TaskStatusPayload = { taskId?: string; TaskID?: string; status?: string; Status?: string };
type TaskStatusEnvelope = TaskStatusPayload & { data?: TaskStatusPayload };
type TaskStatusMessage = TaskStatusPayload & {
	data?: TaskStatusPayload;
	event?: string | TaskStatusEnvelope;
};

function extractTaskStatusUpdate(msg: TaskStatusMessage): { taskId?: string; status?: string } {
	let event: TaskStatusEnvelope | undefined;
	if (typeof msg?.event === "string") {
		try {
			event = JSON.parse(msg.event);
		} catch {
			event = undefined;
		}
	} else {
		event = msg?.event;
	}
	const payload = event?.data ?? msg?.data ?? event ?? msg;
	return {
		taskId: payload?.taskId ?? payload?.TaskID,
		status: payload?.status ?? payload?.Status,
	};
}

type StoreState = {
	taskHistory: undefined | Task[];
	activeTasks: undefined | Task[];
	taskHistoryUpdateStatus: RemoteAccessState;
	activeTaskUpdateStatus: RemoteAccessState;
	taskMetadataHistory: number[] | undefined;
	taskMetadataHistoryFetchInFlight: Promise<void> | undefined;
	taskMetadataHistoryRefetchRequested: boolean;
	taskEventBus: UseEventBusReturn<TaskBusEvent, any>;
	taskListContinuationKey: undefined | string;
	activeTasksListContinuationKey: undefined | string;
	realtimeSubscription: undefined | RealtimeSubscription;
	realtimeConnectInFlight: Promise<boolean> | undefined;
	realtimeTeardownRequested: boolean;
	taskHistoryServedFromCache: boolean;
	taskIDsFetchedOverCache: Raw<Set<string>>;
	taskHistoryInitialisationInFlight: undefined | Promise<void>;
};

const TASK_HISTORY_CACHE_KEY = "task-history";

// Cached lists are untrusted local input: keep only entries the app can actually render.
function isCachedTask(entry: unknown): entry is Task {
	if (typeof entry !== "object" || entry === null) {
		return false;
	}
	const candidate = entry as Partial<Task>;
	return (
		typeof candidate.TaskID === "string" &&
		typeof candidate.Owner === "string" &&
		typeof candidate.BuildID === "string" &&
		typeof candidate.Status === "string" &&
		typeof candidate.CreatedAt === "number" &&
		typeof candidate.UpdatedAt === "number" &&
		Array.isArray(candidate.StatusTransitions)
	);
}

const TASK_ACTIVITY_CACHE_KEY = "taskMetadataHistory";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type CachedTaskActivity = { utcDay: number; counts: number[] };

function isCachedTaskActivity(record: unknown): record is CachedTaskActivity {
	if (typeof record !== "object" || record === null) {
		return false;
	}
	const candidate = record as Partial<CachedTaskActivity>;
	return (
		typeof candidate.utcDay === "number" &&
		Array.isArray(candidate.counts) &&
		candidate.counts.every((count) => typeof count === "number")
	);
}

function utcDayOf(date: Date): number {
	return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function realignToUTCDay({ utcDay, counts }: CachedTaskActivity, todayUTC: number): number[] | undefined {
	const elapsedDays = Math.round((todayUTC - utcDay) / MS_PER_DAY);
	if (elapsedDays < 0 || elapsedDays >= counts.length) {
		return undefined;
	}
	return [...new Array(elapsedDays).fill(0), ...counts.slice(0, counts.length - elapsedDays)];
}

/*
 * given n = tasks returned per request, the SCDP will try to look for
 * active tasks in the first (n * activeTaskMaxFetchDepth) tasks in the databse
 * at the time of writing n = 50
 */
const activeTaskMaxFetchDepth = 3;

export const useTasksStore = defineStore("tasks", {
	state: (): StoreState => ({
		taskHistory: undefined,
		activeTasks: undefined,
		taskHistoryUpdateStatus: { loading: false, error: false, message: "" },
		activeTaskUpdateStatus: { loading: false, error: false, message: "" },
		taskListContinuationKey: undefined,
		activeTasksListContinuationKey: undefined,
		taskMetadataHistory: undefined,
		taskMetadataHistoryFetchInFlight: undefined,
		taskMetadataHistoryRefetchRequested: false,
		taskEventBus: useEventBus(taskKey),
		realtimeSubscription: undefined,
		realtimeConnectInFlight: undefined,
		realtimeTeardownRequested: false,
		taskHistoryServedFromCache: false,
		taskIDsFetchedOverCache: markRaw(new Set<string>()),
		taskHistoryInitialisationInFlight: undefined,
	}),
	getters: {
		getTaskByProjectID: (state) => (projectID: string) => {
			// ! Deprecated
			// console.warn("using deprecated function (getTaskByProjectID) :>> ");
			if (!state.taskHistory) {
				return undefined;
			}
			return state.taskHistory.find((x) => x.TaskID === projectID) ?? null;
		},
		getTaskByID: (state) => (taskID: string) => {
			if (!state.taskHistory || !taskID) {
				return undefined;
			}
			return state.taskHistory.find((x) => x.TaskID === taskID) ?? null;
		},
		// Stale once a refresh failed, and still stale while the retry runs.
		taskHistoryIsStale: (state): boolean => state.taskHistoryServedFromCache && state.taskHistoryUpdateStatus.error,
		inactiveTasks(): Task[] {
			return (this.taskHistory ?? [])
				.filter((x) => !isTaskActive(x.Status))
				.sort(
					util.byNewestFirst(
						(task) => task.FinishedAt || task.CreatedAt,
						(task) => task.TaskID,
					),
				);
		},
		sortedActiveTasks(): Task[] {
			// Optimistic tasks arrive without CreatedAt
			return [...(this.activeTasks ?? [])].sort(
				util.byNewestFirst(
					(task) => task.CreatedAt || Infinity,
					(task) => task.TaskID,
				),
			);
		},
	},
	actions: {
		async getTaskByIdOrFetch(taskID: string): Promise<Task> {
			const localTask = this.getTaskByID(taskID);
			if (localTask && (!this.taskHistoryServedFromCache || this.taskIDsFetchedOverCache.has(taskID))) {
				return localTask;
			}
			const response = await sdk.tasks.getOne(taskID);
			const taskFromRemote = response as Task;

			// A lone task is not a history, and the cache would save it as one
			if (this.taskHistory) {
				this.taskHistory = util.mergeByID(this.taskHistory, [taskFromRemote], (task) => task.TaskID);
				this.taskIDsFetchedOverCache.add(taskID);
			}
			return taskFromRemote;
		},
		updateTaskByID(taskData: Task) {
			if (this.taskHistory) {
				this.taskHistory = util.mergeByID(this.taskHistory, [taskData], (task) => task.TaskID);
			}
			if (this.activeTasks) {
				this.activeTasks = util.mergeByID(this.activeTasks, [taskData], (task) => task.TaskID);
			}
		},
		removeTaskByIdFromLocal(taskID: string) {
			if (this.taskHistory) {
				this.taskHistory = this.taskHistory.filter((x) => x.TaskID !== taskID);
			}
			this.removeFromActiveTaskList(taskID);
		},
		updateTaskList(taskData: Task) {
			// ! deprecated
			// console.warn("using deprecated function (updateTaskList) :>> ");
			if (this.taskHistory) {
				this.taskHistory = util.mergeByID(this.taskHistory, [taskData], (task) => task.TaskID);
			}
		},
		removeFromActiveTaskList(taskID: string) {
			if (this.activeTasks) {
				this.activeTasks = this.activeTasks.filter((x) => x.TaskID !== taskID);
			}
		},
		addToActiveTaskList(taskData: Task) {
			if (!this.activeTasks) {
				return;
			}
			// Dedup by TaskID so the optimistic add can't duplicate a WS-pushed task.
			this.activeTasks = util.mergeByID(this.activeTasks, [taskData], (task) => task.TaskID);
		},
		// Paint the cached history immediately. The fetch below replaces it once it lands.
		async hydrateTaskHistoryFromCache(historyAnswered: () => boolean = () => false) {
			const painted = await hydrateCachedList(
				TASK_HISTORY_CACHE_KEY,
				isCachedTask,
				() => this.taskHistory,
				(task) => task.TaskID,
			);
			if (painted && !historyAnswered()) {
				this.taskHistory = painted;
				this.taskHistoryServedFromCache = true;
				this.taskIDsFetchedOverCache.clear();
			}
		},
		async initialiseTaskHistory() {
			this.taskHistoryInitialisationInFlight ??= (async () => {
				localStorage.removeItem("task-list");
				mirrorStoreListToCache(
					this,
					TASK_HISTORY_CACHE_KEY,
					() =>
						this.taskHistory &&
						[...this.taskHistory].sort(
							util.byNewestFirst(
								(task) => task.CreatedAt,
								(task) => task.TaskID,
							),
						),
					LIST_CACHE_MAX_ENTRIES,
				);
				// Reset keys: the first fresh batch replaces the cached list rather than appending.
				this.taskListContinuationKey = undefined;
				this.activeTasksListContinuationKey = undefined;
				let historyAnswered = false;
				await Promise.all([
					this.hydrateTaskHistoryFromCache(() => historyAnswered),
					this.fetchNextTaskBatchFromRemote(() => {
						historyAnswered = true;
					}),
				]);
				await this.fetchActiveTasksFromRemote();
			})().finally(() => {
				this.taskHistoryInitialisationInFlight = undefined;
			});
			return this.taskHistoryInitialisationInFlight;
		},

		// Refetch the active list from scratch to recover from missed
		// WS status pushes (sleeping tab, dropped messages, etc.).
		async reconcileActiveTasks() {
			if (this.activeTaskUpdateStatus.loading) return;
			// IDs that drop out of the active list have terminated; resolve each so
			// the status bus and history converge even when the WS missed the push.
			const previousStatuses = new Map((this.activeTasks ?? []).map((task) => [task.TaskID, task.Status]));
			const previouslyActiveIds = [...previousStatuses.keys()];
			this.activeTasksListContinuationKey = undefined;
			await this.fetchActiveTasksFromRemote();
			for (const task of this.activeTasks ?? []) {
				const previousStatus = previousStatuses.get(task.TaskID);
				if (previousStatus !== undefined && previousStatus !== task.Status) this.handleTaskUpdates(task);
			}
			const stillActiveIds = new Set((this.activeTasks ?? []).map((task) => task.TaskID));
			const terminatedIds = previouslyActiveIds.filter((taskID) => !stillActiveIds.has(taskID));
			await Promise.all(
				terminatedIds.map(async (taskID) => {
					// Skip if the WS already converged it (avoid double-firing completion).
					const known = this.getTaskByID(taskID);
					if (known && isTaskTerminated(known.Status)) return;
					try {
						const finalTask = await sdk.tasks.getOne(taskID);
						this.handleTaskUpdates(finalTask as Task);
					} catch (error) {
						Sentry.captureException(error);
					}
				}),
			);
		},
		async fetchActiveTasksFromRemote() {
			try {
				this.activeTaskUpdateStatus.loading = true;
				let requestCounter = 0;
				do {
					await this.fetchNextActiveTaskBatchFromRemote();
					requestCounter += 1;
				} while (requestCounter < activeTaskMaxFetchDepth && this.activeTasksListContinuationKey !== undefined);
			} catch (error) {
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					this.activeTasks = [];
				}
			} finally {
				this.activeTaskUpdateStatus.loading = false;
			}
		},
		async fetchNextActiveTaskBatchFromRemote() {
			try {
				const response = await this.fetchTasksFromRemote(this.activeTasksListContinuationKey, [
					"Accepted",
					"Initialising",
					"In Progress",
				]);

				// Always assign (even on empty) so a finished last task can't linger.
				const fetchedActiveTasks = (response.Tasks as Task[]) ?? [];
				// Dedup by TaskID: concurrent fetches share the continuation key and can re-concat a task.
				this.activeTasks =
					this.activeTasks && this.activeTasksListContinuationKey
						? util.mergeByID(this.activeTasks, fetchedActiveTasks, (task) => task.TaskID)
						: fetchedActiveTasks;
				this.activeTasksListContinuationKey = response.ContinuationKey;
				this.activeTaskUpdateStatus.message = "";
			} catch (error) {
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					this.activeTasks = [];
				}
				util.recordListFetchError(
					error,
					this.activeTaskUpdateStatus,
					TASK_LIST_FORBIDDEN_MESSAGE,
					"Get active tasks from remote",
				);
			}
		},

		async fetchNextTaskBatchFromRemote(onAnswered?: () => void) {
			try {
				this.taskHistoryUpdateStatus.loading = true;
				const response = await this.fetchTasksFromRemote(this.taskListContinuationKey);
				// The refresh answered, whatever shape it came back in.
				onAnswered?.();
				this.taskHistoryServedFromCache = false;
				const fetchedTasks = (response.Tasks as Task[] | undefined) ?? [];
				this.taskHistory =
					this.taskHistory && this.taskListContinuationKey
						? util.mergeByID(this.taskHistory, fetchedTasks, (task) => task.TaskID)
						: fetchedTasks;
				this.taskListContinuationKey = response.ContinuationKey;
				this.taskHistoryUpdateStatus.message = "";
			} catch (error) {
				const answered =
					error instanceof SdkError &&
					(error.code === ERROR_CODES.API_FORBIDDEN || error.code === ERROR_CODES.API_NOT_FOUND);
				if (answered) {
					onAnswered?.();
				}
				if (
					error instanceof SdkError &&
					error.code === ERROR_CODES.API_FORBIDDEN &&
					this.taskHistoryServedFromCache
				) {
					this.taskHistory = undefined;
					this.taskHistoryServedFromCache = false;
				}
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					this.taskHistory = [];
					this.taskHistoryServedFromCache = false;
				}
				util.recordListFetchError(
					error,
					this.taskHistoryUpdateStatus,
					TASK_LIST_FORBIDDEN_MESSAGE,
					"Get tasks from remote",
				);
			} finally {
				this.taskHistoryUpdateStatus.loading = false;
			}
		},

		async fetchTasksFromRemote(startKey?: string, taskStatus?: TaskStatus[]) {
			const authUserStore = useUserStore();
			await util.waitFor(() => authUserStore.currentUserSessionObject !== undefined);
			return sdk.tasks.list({
				startKey,
				status: taskStatus as SdkTaskStatus[] | undefined,
			});
		},
		async getTasksMetadataFromRemote(
			from?: string,
			to?: string,
			startKey?: string,
		): Promise<{ PartialTasks: PartialTask[]; ContinuationKey?: string } | null> {
			const authUserStore = useUserStore();
			await util.waitFor(() => authUserStore.currentUserSessionObject !== undefined);
			try {
				const response = await sdk.tasks.listSummary({ from, to, startKey });
				return {
					PartialTasks: response.Tasks as PartialTask[],
					ContinuationKey: response.ContinuationKey,
				};
			} catch (error) {
				Sentry.captureException(error);
				if (error instanceof SdkError) {
					console.log("Tasks summary fetch failed:", error.code, error.message);
				}
				return null;
			}
		},
		getTasksMetadataHistoryWithinDays(days: number = 7): Promise<void> {
			if (this.taskMetadataHistoryFetchInFlight) {
				this.taskMetadataHistoryRefetchRequested = true;
				return this.taskMetadataHistoryFetchInFlight;
			}
			const run = (async () => {
				try {
					await Promise.all([this.showCachedTaskActivity(), this.refreshTasksMetadataHistory(days)]);
					this.taskMetadataHistory ??= [];
				} finally {
					this.taskMetadataHistoryFetchInFlight = undefined;
					if (this.taskMetadataHistoryRefetchRequested) {
						this.taskMetadataHistoryRefetchRequested = false;
						this.getTasksMetadataHistoryWithinDays(days);
					}
				}
			})();
			this.taskMetadataHistoryFetchInFlight = run;
			return run;
		},
		async refreshTasksMetadataHistory(days: number) {
			const today = new Date();
			const from = new Date(
				Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - (days - 1), 0, 0, 0, 0),
			).toISOString();
			const to = new Date(
				Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 23, 59, 59, 999),
			).toISOString();

			const tasks: PartialTask[] = [];
			let startKey: string | undefined;
			do {
				const page = await this.getTasksMetadataFromRemote(from, to, startKey);
				if (!page) {
					return;
				}
				tasks.push(...page.PartialTasks);
				startKey = page.ContinuationKey;
			} while (startKey);

			const counts = this.bucketTasksByDay(tasks, days, today);
			this.taskMetadataHistory = counts;
			writeCachedRecord(TASK_ACTIVITY_CACHE_KEY, { utcDay: utcDayOf(today), counts });
		},
		async showCachedTaskActivity() {
			if (this.taskMetadataHistory !== undefined) {
				return;
			}
			const cachedActivity = await readCachedRecord(TASK_ACTIVITY_CACHE_KEY, isCachedTaskActivity);
			this.taskMetadataHistory ??= cachedActivity && realignToUTCDay(cachedActivity, utcDayOf(new Date()));
		},
		bucketTasksByDay(tasks: PartialTask[], days: number, today: Date): number[] {
			// Initialize counts array: index 0 = today, index 1 = yesterday, etc.
			const counts = new Array(days).fill(0);
			const todayUTC = utcDayOf(today);

			tasks.forEach((task) => {
				const taskDate = new Date(util.convertToMilliseconds(task.CreatedAt));

				// Calculate which day bucket this task belongs to
				const daysDiff = Math.floor((todayUTC - utcDayOf(taskDate)) / MS_PER_DAY);

				if (daysDiff >= 0 && daysDiff < days) {
					counts[daysDiff]++;
				}
			});

			return counts;
		},
		/*
		 *	Subscribe to the user's realtime task-status channel. The push
		 *	carries taskId + status. On non-terminal transitions we merge the
		 *	status delta into the cached record and skip the REST refetch; on
		 *	terminal status (or first sighting with no cached record) we
		 *	refetch to pick up fields the WS payload doesn't carry (output
		 *	streams, final stats). Idempotent — a second call while a
		 *	subscription is live is a no-op.
		 */
		initRealtime(userId: string): Promise<boolean> {
			if (this.realtimeSubscription) return Promise.resolve(true);
			if (!this.realtimeConnectInFlight) {
				this.realtimeConnectInFlight = this.connectRealtime(userId).finally(() => {
					this.realtimeConnectInFlight = undefined;
				});
			}
			return this.realtimeConnectInFlight;
		},
		async connectRealtime(userId: string): Promise<boolean> {
			this.realtimeTeardownRequested = false;
			let subscription: RealtimeSubscription;
			try {
				await sdk.realtime.connect();
				subscription = await sdk.realtime.channel(`task-status/${idToChannelName(userId)}`).subscribe();
			} catch (err) {
				console.error("Failed to initialize realtime task channel:", err);
				if (sdk.realtime.getState() === "open") useRealtimeStore().reportChannelFailure(err);
				return false;
			}
			// Honor a teardown that raced this connect; otherwise the subscription leaks.
			if (this.realtimeTeardownRequested) {
				subscription.close();
				return false;
			}
			this.realtimeSubscription = subscription;

			subscription.on("message", async (msg: TaskStatusMessage) => {
				const { taskId, status } = extractTaskStatusUpdate(msg);
				if (!taskId) return;

				const cached = this.getTaskByID(taskId) ?? this.activeTasks?.find((task) => task.TaskID === taskId);
				// Ignore pushes for an already-terminal task so we don't re-fire completion.
				if (cached && isTaskTerminated(cached.Status)) {
					return;
				}
				const isTerminal = !!status && isTaskTerminated(status as TaskStatus);
				if (cached && status && !isTerminal) {
					this.handleTaskUpdates({ ...cached, Status: status as TaskStatus });
					return;
				}

				try {
					const response = await sdk.tasks.getOne(taskId);
					this.handleTaskUpdates(response as Task);
				} catch (err) {
					Sentry.captureException(err);
				}
			});
			subscription.on("error", (err: unknown) => {
				console.error("Realtime task channel error:", err);
				if (isRealtimeGiveUpError(err)) return;
				subscription.close();
				this.realtimeSubscription = undefined;
				useRealtimeStore().reportChannelFailure(err);
			});

			// No snapshot on subscribe: fetch once on connect so an already-running task is not missed.
			await this.fetchActiveTasksFromRemote();
			return true;
		},
		teardownRealtime() {
			// Mark teardown so an in-flight initRealtime() closes its not-yet-assigned subscription.
			this.realtimeTeardownRequested = true;
			if (this.realtimeSubscription) {
				this.realtimeSubscription.close();
				this.realtimeSubscription = undefined;
			}
		},
		handleTaskUpdates(taskData: Task) {
			// update the task object in task history
			this.updateTaskByID(taskData);

			// Check if task is terminated/completed
			if (isTaskTerminated(taskData.Status)) {
				this.removeFromActiveTaskList(taskData.TaskID);

				this.taskEventBus.emit({ type: TaskEventE.TaskCompleted, taskData: taskData });

				const rootStore = useRootStore();
				rootStore.tierLimitEventBus.emit({
					type: TierLimitEventTypeE.UsageChanged,
					affectedLimits: [UserLimitsE.ConcurrentTaskCount, UserLimitsE.ExecutionTimeInMillisecondsCount],
				});
				// Update histogram
				this.getTasksMetadataHistoryWithinDays();
				return;
			}
			// Update status of tasks of ongoing tasks
			this.taskEventBus.emit({ type: TaskEventE.TaskUpdated, taskData: taskData });
		},
	},
});
